// ADDED FOR THEME — global theme state (light / dark / system).
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** The user's saved preference. */
  theme: ThemePreference;
  /** The theme actually applied to the DOM right now. */
  effectiveTheme: EffectiveTheme;
  setTheme: (theme: ThemePreference) => void;
  /** Cycles system → light → dark → system. */
  toggleTheme: () => void;
}

const STORAGE_KEY = 'teskedo-theme';

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

function resolve(pref: ThemePreference): EffectiveTheme {
  return pref === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : pref;
}

/** Applies (or removes) the `dark` class on <html>. */
function applyToDom(effective: EffectiveTheme) {
  const root = document.documentElement;
  root.classList.toggle('dark', effective === 'dark');
  root.style.colorScheme = effective; // native form controls / scrollbars
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() => readStored());
  const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(() => resolve(readStored()));

  // Apply on mount + whenever the preference changes.
  useEffect(() => {
    const eff = resolve(theme);
    setEffectiveTheme(eff);
    applyToDom(eff);
  }, [theme]);

  // When following the system, react to OS-level changes live.
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const eff: EffectiveTheme = mq.matches ? 'dark' : 'light';
      setEffectiveTheme(eff);
      applyToDom(eff);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: ThemePreference = prev === 'system' ? 'light' : prev === 'light' ? 'dark' : 'system';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, effectiveTheme, setTheme, toggleTheme }),
    [theme, effectiveTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
